import { KPIFilterDropdownItem } from '../../../AnalyticsHub/components/KPIFilterDropdown';
import type {
  ChartItem,
} from './components/VerticalBarChartCard';
import type { AnalyticsChartRow } from './components/AnalyticsChartCard';
import type {
  LifecycleStaleTicket,
  SubmittersLocationData,
  SubmittersMasterLocationData,
  SubmittersRepeatSubmitterData,
} from './components/types';

export const CREWING_ANALYTICS_REPORT_LINK = '/navigator-reports/crewing-analytics';

export type CrewingTabKey = 'executive-overview' | 'efficiency-breakdown' | 'hours-breakdown' | 'crewing-execution';

export interface CrewingTabItem {
  key: CrewingTabKey;
  label: string;
}

export const CREWING_TABS: CrewingTabItem[] = [
  { key: 'executive-overview', label: 'Executive Overview' },
  { key: 'efficiency-breakdown', label: 'Efficiency Breakdown' },
  { key: 'hours-breakdown', label: 'Hours Breakdown' },
  { key: 'crewing-execution', label: 'Crewing Execution' },
];

export const REGION_OPTIONS: KPIFilterDropdownItem[] = [
  { id: 'north', displayText: 'North Region' },
  { id: 'south', displayText: 'South Region' },
  { id: 'west', displayText: 'West Region' },
];

export const MARKET_OPTIONS: KPIFilterDropdownItem[] = [
  { id: 'dallas', displayText: 'Dallas' },
  { id: 'chicago', displayText: 'Chicago' },
  { id: 'phoenix', displayText: 'Phoenix' },
  { id: 'nashville', displayText: 'Nashville' },
];

export const LOCATION_OPTIONS: KPIFilterDropdownItem[] = [
  { id: 'dal-wh-01', displayText: 'DAL WH 01' },
  { id: 'dal-wh-02', displayText: 'DAL WH 02' },
  { id: 'chi-wh-09', displayText: 'CHI WH 09' },
  { id: 'phx-wh-04', displayText: 'PHX WH 04' },
  { id: 'nsh-wh-11', displayText: 'NSH WH 11' },
];

export interface LocationHierarchyNode {
  locationId: string;
  locationName: string;
  market: string;
  region: string;
}

export const LOCATION_HIERARCHY_MAP: LocationHierarchyNode[] = [
  { locationId: 'dal-wh-01', locationName: 'DAL WH 01', market: 'Dallas', region: 'North Region' },
  { locationId: 'dal-wh-02', locationName: 'DAL WH 02', market: 'Dallas', region: 'North Region' },
  { locationId: 'chi-wh-09', locationName: 'CHI WH 09', market: 'Chicago', region: 'North Region' },
  { locationId: 'phx-wh-04', locationName: 'PHX WH 04', market: 'Phoenix', region: 'West Region' },
  { locationId: 'nsh-wh-11', locationName: 'NSH WH 11', market: 'Nashville', region: 'South Region' },
];

export const YEAR_OPTIONS: KPIFilterDropdownItem[] = [
  { id: '2026', displayText: '2026' },
  { id: '2025', displayText: '2025' },
];

export const PERIOD_OPTIONS: KPIFilterDropdownItem[] = Array.from({ length: 13 }, (_, index) => ({
  id: `P${index + 1}`,
  displayText: `P${index + 1}`,
}));

export const WEEK_OPTIONS: KPIFilterDropdownItem[] = Array.from({ length: 12 }, (_, index) => ({
  id: `W${index + 1}`,
  displayText: `W${index + 1}`,
}));

export const PERIOD_WEEK_MAP: Record<string, string[]> = {
  P1: ['W1', 'W2', 'W3', 'W4'],
  P2: ['W5', 'W6', 'W7', 'W8'],
  P3: ['W9', 'W10', 'W11', 'W12'],
  P4: ['W13', 'W14', 'W15', 'W16'],
  P5: ['W17', 'W18', 'W19', 'W20'],
  P6: ['W21', 'W22', 'W23', 'W24'],
  P7: ['W25', 'W26', 'W27', 'W28'],
  P8: ['W29', 'W30', 'W31', 'W32'],
  P9: ['W33', 'W34', 'W35', 'W36'],
  P10: ['W37', 'W38', 'W39', 'W40'],
  P11: ['W41', 'W42', 'W43', 'W44'],
  P12: ['W45', 'W46', 'W47', 'W48'],
  P13: ['W49', 'W50', 'W51', 'W52'],
};

// Full union of DLO's canonical VP_Zone catalog (NON-DSD + DSD), matching the default zone
// lists in src/modules/dlo/components/Tables/{DSD,NonDSD}VolumeZoneDistributionByDay/dataUtils.ts
// — the app's other source of truth for VP zones — instead of an ad-hoc partial list.
export const ZONE_OPTIONS: KPIFilterDropdownItem[] = [
  // NON-DSD zones
  { id: 'receiving', displayText: 'Receiving' },
  { id: 'replen', displayText: 'Replen' },
  { id: 'transfull', displayText: 'Transfull' },
  { id: 'transpick', displayText: 'Transpick' },
  // DSD zones
  { id: 'bay', displayText: 'Bay' },
  { id: 'bulk', displayText: 'Bulk' },
  { id: 'chilled', displayText: 'Chilled' },
  { id: 'full', displayText: 'Full' },
  { id: 'fsv', displayText: 'FSV' },
  { id: 'geo', displayText: 'Geo' },
  { id: 'layer', displayText: 'Layer' },
  { id: 'support', displayText: 'Support' },
];

export const CATEGORY_OPTIONS: KPIFilterDropdownItem[] = [
  { id: 'direct', displayText: 'Direct' },
  { id: 'indirect', displayText: 'Indirect' },
  { id: 'other', displayText: 'Other' },
];

export type CrewingQuickSelectKey = 'wtd' | 'ptd' | 'qtd' | 'ytd' | 'lcw' | 'lcp';

export const QUICK_SELECT_OPTIONS: { key: CrewingQuickSelectKey; label: string }[] = [
  { key: 'wtd', label: 'Week-to-Date' },
  { key: 'ptd', label: 'Period-to-Date' },
  { key: 'qtd', label: 'Quarter-to-Date' },
  { key: 'ytd', label: 'Year-to-Date' },
  { key: 'lcw', label: 'Last Closed Week' },
  { key: 'lcp', label: 'Last Closed Period' },
];

export interface CrewingMetricCardData {
  title: string;
  value: string;
  description?: string;
  valueColor?: string;
  trendText?: string;
  trendDirection?: 'up' | 'down';
  trendImpact?: 'positive' | 'negative' | 'neutral' | null;
  icon?: string;
  iconColor?: string;
}

export const EXECUTIVE_METRICS: CrewingMetricCardData[] = [
  {
    title: 'Crewing Accuracy',
    value: '92.1%',
    description: '1,474 / 1,600 hrs',
    valueColor: '#C12C01',
    icon: 'track-changes',
    iconColor: '#C12C01',
  },
  {
    title: 'Crewing Efficiency',
    value: '89.7%',
    description: '1,435 / 1,600 hrs',
    valueColor: '#C12C01',
    icon: 'show-chart',
    iconColor: '#C12C01',
  },
  {
    title: 'Crewing Compliance',
    value: '96.2%',
    description: '1,566 / 1,628 hrs',
    valueColor: '#0B5FC6',
    icon: 'check-circle',
    iconColor: '#0B5FC6',
  },
  {
    title: 'Estimated KCPH',
    value: '98.0',
    description: '156,800 cs / 1,600 hrs',
    valueColor: '#0B5FC6',
  },
  {
    title: 'Scheduled KCPH',
    value: '101.2',
    description: '164,800 cs / 1,628 hrs',
    valueColor: '#0B5FC6',
  },
  {
    title: 'Actual KCPH',
    value: '103.0',
    description: '164,800 cs / 1,600 hrs',
    valueColor: '#0B5FC6',
  },
  {
    title: 'Crewing Tool Adoption %',
    value: '61.0%',
    description: '214 of 351 locations',
    valueColor: '#C12C01',
  },
];

export const KCPH_BY_REGION: ChartItem[] = [
  { label: 'North', value: 95.8, valueLabel: '95.8%' },
  { label: 'South', value: 91.3, valueLabel: '91.3%' },
  { label: 'West', value: 89.9, valueLabel: '89.9%' },
];

export const SCHEDULE_ADHERENCE_ROWS: AnalyticsChartRow[] = [
  { label: 'Pick', value: 97.2, displayValue: 97.2, percentage: 97.2, color: '#0B5FC6', unit: '%' },
  { label: 'Load', value: 95.6, displayValue: 95.6, percentage: 95.6, color: '#0B5FC6', unit: '%' },
  { label: 'Replen', value: 94.3, displayValue: 94.3, percentage: 94.3, color: '#0B5FC6', unit: '%' },
  { label: 'Forklift', value: 93.5, displayValue: 93.5, percentage: 93.5, color: '#0B5FC6', unit: '%' },
  { label: 'Checker', value: 92.2, displayValue: 92.2, percentage: 92.2, color: '#0B5FC6', unit: '%' },
];

export const OVERTIME_BY_MARKET: ChartItem[] = [
  {
    label: 'Dallas',
    segments: [
      { label: 'Planned', value: 320, color: '#82CDF8' },
      { label: 'Actual', value: 280, color: '#0B5FC6' },
    ],
  },
  {
    label: 'Chicago',
    segments: [
      { label: 'Planned', value: 290, color: '#82CDF8' },
      { label: 'Actual', value: 255, color: '#0B5FC6' },
    ],
  },
  {
    label: 'Phoenix',
    segments: [
      { label: 'Planned', value: 220, color: '#82CDF8' },
      { label: 'Actual', value: 260, color: '#0B5FC6' },
    ],
  },
  {
    label: 'Nashville',
    segments: [
      { label: 'Planned', value: 210, color: '#82CDF8' },
      { label: 'Actual', value: 198, color: '#0B5FC6' },
    ],
  },
];

export const VARIANCE_BY_FUNCTION: AnalyticsChartRow[] = [
  { label: 'Pick', value: 2.8, displayValue: 2.8, percentage: 2.8, color: '#C12C01', unit: '%' },
  { label: 'Load', value: 1.9, displayValue: 1.9, percentage: 1.9, color: '#C12C01', unit: '%' },
  { label: 'Replen', value: 1.4, displayValue: 1.4, percentage: 1.4, color: '#C12C01', unit: '%' },
  { label: 'Forklift', value: 3.2, displayValue: 3.2, percentage: 3.2, color: '#C12C01', unit: '%' },
  { label: 'Checker', value: 1.6, displayValue: 1.6, percentage: 1.6, color: '#C12C01', unit: '%' },
];

export const HOURS_BREAKDOWN_DAILY: ChartItem[] = [
  { label: 'Mon', value: 1850, valueLabel: '1,850' },
  { label: 'Tue', value: 1910, valueLabel: '1,910' },
  { label: 'Wed', value: 1985, valueLabel: '1,985' },
  { label: 'Thu', value: 2020, valueLabel: '2,020' },
  { label: 'Fri', value: 1888, valueLabel: '1,888' },
  { label: 'Sat', value: 1660, valueLabel: '1,660' },
  { label: 'Sun', value: 1540, valueLabel: '1,540' },
];

export const HOURS_MIX_ROWS: AnalyticsChartRow[] = [
  {
    label: 'Regular',
    segments: [
      { label: 'Planned', value: 7150, color: '#82CDF8' },
      { label: 'Actual', value: 6940, color: '#0B5FC6' },
    ],
  },
  {
    label: 'Overtime',
    segments: [
      { label: 'Planned', value: 1320, color: '#F9C2B0' },
      { label: 'Actual', value: 1240, color: '#C12C01' },
    ],
  },
];

export const TOOL_ADOPTION_BY_SITE: ChartItem[] = [
  { label: 'DAL WH 01', value: 91, valueLabel: '91%' },
  { label: 'DAL WH 02', value: 79, valueLabel: '79%' },
  { label: 'CHI WH 09', value: 84, valueLabel: '84%' },
  { label: 'PHX WH 04', value: 73, valueLabel: '73%' },
  { label: 'NSH WH 11', value: 85, valueLabel: '85%' },
];

export const TOOL_USAGE_ROWS: AnalyticsChartRow[] = [
  { label: 'Model Plan Published', value: 100, displayValue: 100, percentage: 100, color: '#0B5FC6', unit: '%' },
  { label: 'Plan Accepted', value: 87, displayValue: 87, percentage: 87, color: '#0B5FC6', unit: '%' },
  { label: 'Shift Lock Complete', value: 81, displayValue: 81, percentage: 81, color: '#0B5FC6', unit: '%' },
  { label: 'Variance Justified', value: 76, displayValue: 76, percentage: 76, color: '#0B5FC6', unit: '%' },
  { label: 'Post-Shift Review', value: 68, displayValue: 68, percentage: 68, color: '#0B5FC6', unit: '%' },
];

export const LOCATION_TABLE_ROWS: SubmittersLocationData[] = [
  {
    location: 'DAL WH 01',
    ticketsSubmitted: 410,
    ticketsClosed: 396,
    ticketsOpen: 14,
    resolutionRate: 96.6,
    averageResolutionTime: 5.2,
    topCategoryName: 'Overtime Spike',
    topCategoryCount: 24,
  },
  {
    location: 'DAL WH 02',
    ticketsSubmitted: 332,
    ticketsClosed: 317,
    ticketsOpen: 15,
    resolutionRate: 95.5,
    averageResolutionTime: 5.8,
    topCategoryName: 'Absentee Fill',
    topCategoryCount: 21,
  },
  {
    location: 'CHI WH 09',
    ticketsSubmitted: 298,
    ticketsClosed: 286,
    ticketsOpen: 12,
    resolutionRate: 96.0,
    averageResolutionTime: 4.9,
    topCategoryName: 'Late Start',
    topCategoryCount: 16,
  },
  {
    location: 'PHX WH 04',
    ticketsSubmitted: 265,
    ticketsClosed: 251,
    ticketsOpen: 14,
    resolutionRate: 94.7,
    averageResolutionTime: 6.1,
    topCategoryName: 'Crew Shortage',
    topCategoryCount: 28,
  },
  {
    location: 'NSH WH 11',
    ticketsSubmitted: 221,
    ticketsClosed: 213,
    ticketsOpen: 8,
    resolutionRate: 96.4,
    averageResolutionTime: 4.6,
    topCategoryName: 'Shift Drift',
    topCategoryCount: 11,
  },
];

export const MASTER_LOCATION_ROWS: SubmittersMasterLocationData[] = [
  {
    sapId: 40211,
    location: 'DAL WH 01',
    ticketsSubmitted: 410,
    ticketsClosed: 396,
    ticketsOpen: 14,
    resolutionRate: 96.6,
    averageResolutionTime: 5.2,
    topCategoryName: 'Overtime Spike',
    topCategoryCount: 24,
  },
  {
    sapId: 40212,
    location: 'DAL WH 02',
    ticketsSubmitted: 332,
    ticketsClosed: 317,
    ticketsOpen: 15,
    resolutionRate: 95.5,
    averageResolutionTime: 5.8,
    topCategoryName: 'Absentee Fill',
    topCategoryCount: 21,
  },
  {
    sapId: 41609,
    location: 'CHI WH 09',
    ticketsSubmitted: 298,
    ticketsClosed: 286,
    ticketsOpen: 12,
    resolutionRate: 96,
    averageResolutionTime: 4.9,
    topCategoryName: 'Late Start',
    topCategoryCount: 16,
  },
  {
    sapId: 42704,
    location: 'PHX WH 04',
    ticketsSubmitted: 265,
    ticketsClosed: 251,
    ticketsOpen: 14,
    resolutionRate: 94.7,
    averageResolutionTime: 6.1,
    topCategoryName: 'Crew Shortage',
    topCategoryCount: 28,
  },
];

export const LEADERS_TABLE_ROWS: SubmittersRepeatSubmitterData[] = [
  {
    user: 'dana.roberts@pepsico.com',
    ticketsSubmitted: 128,
    ticketsClosed: 121,
    ticketsOpen: 7,
    resolutionRate: 94.5,
    averageResolutionTime: 4.1,
    topCategoryName: 'Coverage Recovery',
    topCategoryCount: 13,
    navigatorRole: 'Warehouse Manager',
    location: 'DAL WH 01',
  },
  {
    user: 'eric.mills@pepsico.com',
    ticketsSubmitted: 116,
    ticketsClosed: 111,
    ticketsOpen: 5,
    resolutionRate: 95.7,
    averageResolutionTime: 3.8,
    topCategoryName: 'Shift Realignment',
    topCategoryCount: 11,
    navigatorRole: 'Labor Planner',
    location: 'CHI WH 09',
  },
  {
    user: 'sara.khan@pepsico.com',
    ticketsSubmitted: 102,
    ticketsClosed: 97,
    ticketsOpen: 5,
    resolutionRate: 95.1,
    averageResolutionTime: 4.5,
    topCategoryName: 'OT Reduction',
    topCategoryCount: 10,
    navigatorRole: 'Operations Lead',
    location: 'PHX WH 04',
  },
];

export const CREWING_EXCEPTION_TICKETS: LifecycleStaleTicket[] = [
  {
    ticketNumber: 'CRW-10021',
    ticketTitle: 'Unfilled loader role for PM shift',
    category: 'Staffing',
    categoryName: 'Staffing',
    subCategory: 'Loader',
    subCategoryId: 1,
    subCategoryName: 'Loader',
    urgency: 'High',
    currentStatus: 'In Progress',
    age: '3',
    submitted: '2026-06-01T10:20:00.000Z',
    assignedTo: 'dana.roberts@pepsico.com',
  },
  {
    ticketNumber: 'CRW-10037',
    ticketTitle: 'Overtime threshold exceeded in picking',
    category: 'Hours',
    categoryName: 'Hours',
    subCategory: 'Overtime',
    subCategoryId: 2,
    subCategoryName: 'Overtime',
    urgency: 'Critical',
    currentStatus: 'Escalated to IT',
    age: '5',
    submitted: '2026-05-30T16:00:00.000Z',
    assignedTo: 'eric.mills@pepsico.com',
  },
  {
    ticketNumber: 'CRW-10058',
    ticketTitle: 'Planned vs actual headcount mismatch',
    category: 'Execution',
    categoryName: 'Execution',
    subCategory: 'Variance',
    subCategoryId: 3,
    subCategoryName: 'Variance',
    urgency: 'Medium',
    currentStatus: 'Waiting on User',
    age: '2',
    submitted: '2026-06-02T08:15:00.000Z',
    assignedTo: 'sara.khan@pepsico.com',
  },
];

export const LAST_REFRESHED_LABEL = 'Jun 4, 2026 · 6:41 PM';

// ========== SCHEDULE HEALTH & TRENDING DATA ==========

export interface HeatTableRow {
  label: string;
  accuracy: number;
  efficiency: number;
  compliance: number;
  kcphIndex: number;
}

export const SCHEDULE_HEALTH_ROWS: HeatTableRow[] = [
  { label: 'North Region', accuracy: 92.1, efficiency: 89.7, compliance: 96.2, kcphIndex: 98.0 },
  { label: 'South Region', accuracy: 88.5, efficiency: 86.2, compliance: 93.8, kcphIndex: 92.5 },
  { label: 'West Region', accuracy: 94.3, efficiency: 91.5, compliance: 97.1, kcphIndex: 103.2 },
];

export interface SchedulingTrendData {
  periods: string[];
  accuracy: number[];
  efficiency: number[];
  compliance: number[];
  kcphIndex: number[];
}

export const SCHEDULING_TREND: SchedulingTrendData = {
  periods: ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5'],
  accuracy: [89.5, 90.2, 91.8, 92.1, 91.5],
  efficiency: [87.3, 88.1, 89.4, 89.7, 88.9],
  compliance: [94.2, 95.1, 95.8, 96.2, 95.9],
  kcphIndex: [96.0, 97.2, 98.5, 98.0, 99.3],
};

export interface KCPHGroupedRow {
  label: string;
  estimated: number;
  scheduled: number;
  actual: number;
}

export const KCPH_PERFORMANCE_ROWS: KCPHGroupedRow[] = [
  { label: 'North Region', estimated: 98.0, scheduled: 101.2, actual: 103.0 },
  { label: 'South Region', estimated: 92.5, scheduled: 95.1, actual: 96.8 },
  { label: 'West Region', estimated: 103.2, scheduled: 106.5, actual: 108.1 },
];

export interface BulletChartRow {
  label: string;
  value: number;
  target?: number;
  marker?: number;
}

export const TOOL_ADOPTION_BULLET: BulletChartRow[] = [
  { label: 'North Region', value: 68.5, target: 80 },
  { label: 'South Region', value: 54.2, target: 80 },
  { label: 'West Region', value: 71.8, target: 80 },
];

export type ChartDrillLevel = 'region' | 'market' | 'location';
export type ChartTimeframeGrain = 'year' | 'period' | 'week' | 'day';

// Shared x-axis label parsing for every Crewing Analytics trend chart (Scheduling Trend, KCPH
// Trend, Tool Adoption/Execution Trend) so they all render the same P#/W#/plain-year convention
// with an optional 'YY sub-label, instead of each chart formatting the raw backend string itself.
// Backend values are sometimes a bare index (e.g. "P3") and sometimes a year+index blob
// (e.g. "P20251" = period 1 of 2025, "P202510" = period 10 of 2025) — both are handled.
export interface TimeframeLabel {
  main: string;
  sub?: string;
}

export const parseTimeframeLabel = (raw: string, timeframe: ChartTimeframeGrain): TimeframeLabel => {
  const trimmed = raw.trim();

  if (timeframe === 'year') {
    const digits = trimmed.replace(/\D/g, '');
    return { main: digits || trimmed };
  }

  if (timeframe === 'period' || timeframe === 'week') {
    const prefix = timeframe === 'period' ? 'P' : 'W';
    const digits = trimmed.replace(/^[A-Za-z]+/, '').replace(/\D/g, '');

    // 5-6 digits = year (first 4) + index (remaining 1-2 digits)
    if (digits.length >= 5) {
      const year = digits.slice(0, 4);
      const index = digits.slice(4).replace(/^0+(?=\d)/, '');
      return { main: `${prefix}${index || '0'}`, sub: `'${year.slice(-2)}` };
    }

    return { main: `${prefix}${digits || trimmed.replace(/^[A-Za-z]+/i, '')}` };
  }

  return { main: trimmed };
};

const SCHEDULE_HEALTH_BY_DRILL: Record<ChartDrillLevel, HeatTableRow[]> = {
  region: [
    { label: 'North Region', accuracy: 92.1, efficiency: 89.7, compliance: 96.2, kcphIndex: 98.0 },
    { label: 'South Region', accuracy: 88.5, efficiency: 86.2, compliance: 93.8, kcphIndex: 92.5 },
    { label: 'West Region', accuracy: 94.3, efficiency: 91.5, compliance: 97.1, kcphIndex: 103.2 },
  ],
  market: [
    { label: 'Dallas', accuracy: 93.2, efficiency: 90.1, compliance: 96.8, kcphIndex: 99.4 },
    { label: 'Chicago', accuracy: 90.4, efficiency: 88.0, compliance: 94.5, kcphIndex: 96.1 },
    { label: 'Phoenix', accuracy: 87.6, efficiency: 85.8, compliance: 92.7, kcphIndex: 93.4 },
    { label: 'Nashville', accuracy: 91.7, efficiency: 89.2, compliance: 95.6, kcphIndex: 97.8 },
  ],
  location: [
    { label: 'DAL WH 01', accuracy: 94.0, efficiency: 91.2, compliance: 97.3, kcphIndex: 101.2 },
    { label: 'DAL WH 02', accuracy: 91.1, efficiency: 88.9, compliance: 95.1, kcphIndex: 96.0 },
    { label: 'CHI WH 09', accuracy: 89.8, efficiency: 87.6, compliance: 94.0, kcphIndex: 95.2 },
    { label: 'PHX WH 04', accuracy: 86.9, efficiency: 84.5, compliance: 92.1, kcphIndex: 91.9 },
    { label: 'NSH WH 11', accuracy: 92.6, efficiency: 90.0, compliance: 96.0, kcphIndex: 99.0 },
  ],
};

const KCPH_PERFORMANCE_BY_DRILL: Record<ChartDrillLevel, KCPHGroupedRow[]> = {
  region: [
    { label: 'North Region', estimated: 98.0, scheduled: 101.2, actual: 103.0 },
    { label: 'South Region', estimated: 92.5, scheduled: 95.1, actual: 96.8 },
    { label: 'West Region', estimated: 103.2, scheduled: 106.5, actual: 108.1 },
  ],
  market: [
    { label: 'Dallas', estimated: 100.4, scheduled: 103.6, actual: 104.8 },
    { label: 'Chicago', estimated: 96.1, scheduled: 98.9, actual: 100.2 },
    { label: 'Phoenix', estimated: 90.5, scheduled: 93.8, actual: 95.0 },
    { label: 'Nashville', estimated: 95.8, scheduled: 98.4, actual: 99.6 },
  ],
  location: [
    { label: 'DAL WH 01', estimated: 101.0, scheduled: 104.2, actual: 105.6 },
    { label: 'DAL WH 02', estimated: 97.2, scheduled: 100.1, actual: 101.3 },
    { label: 'CHI WH 09', estimated: 94.4, scheduled: 97.0, actual: 98.1 },
    { label: 'PHX WH 04', estimated: 89.7, scheduled: 92.2, actual: 93.6 },
    { label: 'NSH WH 11', estimated: 96.8, scheduled: 99.0, actual: 100.4 },
  ],
};

const TOOL_ADOPTION_BY_DRILL: Record<ChartDrillLevel, BulletChartRow[]> = {
  region: [
    { label: 'North Region', value: 68.5, target: 80 },
    { label: 'South Region', value: 54.2, target: 80 },
    { label: 'West Region', value: 71.8, target: 80 },
  ],
  market: [
    { label: 'Dallas', value: 73.4, target: 80 },
    { label: 'Chicago', value: 66.8, target: 80 },
    { label: 'Phoenix', value: 58.2, target: 80 },
    { label: 'Nashville', value: 70.6, target: 80 },
  ],
  location: [
    { label: 'DAL WH 01', value: 91.0, target: 80 },
    { label: 'DAL WH 02', value: 79.0, target: 80 },
    { label: 'CHI WH 09', value: 84.0, target: 80 },
    { label: 'PHX WH 04', value: 73.0, target: 80 },
    { label: 'NSH WH 11', value: 85.0, target: 80 },
  ],
};

const SCHEDULING_TREND_BY_DRILL_TIMEFRAME: Record<ChartDrillLevel, Record<ChartTimeframeGrain, SchedulingTrendData>> = {
  region: {
    year: {
      periods: ['2025', '2026'],
      accuracy: [90.1, 92.1],
      efficiency: [87.9, 89.7],
      compliance: [95.1, 96.2],
      kcphIndex: [96.8, 98.0],
    },
    period: {
      periods: ['P1', 'P2', 'P3', 'P4', 'P5', 'P6'],
      accuracy: [90.2, 91.0, 90.8, 91.6, 92.1, 91.7],
      efficiency: [88.0, 88.4, 88.7, 89.2, 89.7, 89.1],
      compliance: [95.0, 95.4, 95.6, 96.0, 96.2, 95.8],
      kcphIndex: [96.4, 97.0, 97.2, 97.8, 98.0, 98.4],
    },
    week: {
      periods: ['W17', 'W18', 'W19', 'W20', 'W21'],
      accuracy: [89.5, 90.2, 91.8, 92.1, 91.5],
      efficiency: [87.3, 88.1, 89.4, 89.7, 88.9],
      compliance: [94.2, 95.1, 95.8, 96.2, 95.9],
      kcphIndex: [96.0, 97.2, 98.5, 98.0, 99.3],
    },
    day: {
      periods: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      accuracy: [90.3, 91.1, 91.7, 92.0, 91.6, 90.8, 90.2],
      efficiency: [88.2, 88.6, 89.0, 89.3, 89.0, 88.5, 88.0],
      compliance: [95.3, 95.8, 96.0, 96.2, 95.9, 95.4, 95.1],
      kcphIndex: [97.0, 97.6, 98.1, 98.3, 98.0, 97.4, 97.1],
    },
  },
  market: {
    year: {
      periods: ['2025', '2026'],
      accuracy: [89.5, 91.3],
      efficiency: [87.0, 88.8],
      compliance: [94.3, 95.7],
      kcphIndex: [95.7, 97.4],
    },
    period: {
      periods: ['P1', 'P2', 'P3', 'P4', 'P5', 'P6'],
      accuracy: [89.7, 90.3, 90.6, 91.0, 91.3, 90.9],
      efficiency: [87.2, 87.6, 88.0, 88.4, 88.8, 88.3],
      compliance: [94.4, 94.8, 95.1, 95.4, 95.7, 95.3],
      kcphIndex: [95.9, 96.3, 96.7, 97.1, 97.4, 97.0],
    },
    week: {
      periods: ['W17', 'W18', 'W19', 'W20', 'W21'],
      accuracy: [88.3, 90.7, 87.0, 85.1, 89.4],
      efficiency: [84.8, 87.6, 85.9, 83.2, 88.1],
      compliance: [95.0, 97.8, 94.1, 92.0, 98.6],
      kcphIndex: [95.5, 96.9, 95.8, 94.4, 97.5],
    },
    day: {
      periods: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      accuracy: [89.0, 89.8, 90.7, 91.1, 90.9, 89.7, 89.1],
      efficiency: [87.3, 87.9, 88.4, 88.8, 88.6, 87.8, 87.4],
      compliance: [94.7, 95.1, 95.5, 95.8, 95.6, 95.0, 94.8],
      kcphIndex: [96.2, 96.7, 97.0, 97.2, 97.0, 96.5, 96.3],
    },
  },
  location: {
    year: {
      periods: ['2025', '2026'],
      accuracy: [88.9, 90.7],
      efficiency: [86.4, 88.1],
      compliance: [93.9, 95.3],
      kcphIndex: [95.1, 96.9],
    },
    period: {
      periods: ['P1', 'P2', 'P3', 'P4', 'P5', 'P6'],
      accuracy: [89.1, 89.6, 90.0, 90.4, 90.7, 90.2],
      efficiency: [86.7, 87.1, 87.5, 87.8, 88.1, 87.7],
      compliance: [94.0, 94.3, 94.7, 95.0, 95.3, 94.9],
      kcphIndex: [95.3, 95.8, 96.2, 96.5, 96.9, 96.4],
    },
    week: {
      periods: ['W17', 'W18', 'W19', 'W20', 'W21'],
      accuracy: [91.6, 93.8, 89.9, 84.7, 92.4],
      efficiency: [87.9, 90.4, 88.0, 82.6, 91.2],
      compliance: [98.4, 99.1, 96.7, 91.3, 98.2],
      kcphIndex: [96.7, 98.1, 97.2, 94.0, 98.4],
    },
    day: {
      periods: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      accuracy: [90.1, 90.8, 91.9, 92.5, 91.7, 90.2, 89.8],
      efficiency: [87.0, 87.7, 88.5, 89.0, 88.4, 87.3, 86.9],
      compliance: [94.8, 95.3, 95.9, 96.1, 95.6, 95.0, 94.6],
      kcphIndex: [96.1, 96.6, 97.4, 97.8, 97.2, 96.4, 96.0],
    },
  },
};

export const getScheduleHealthDataByDrillLevel = (drillLevel: ChartDrillLevel): HeatTableRow[] =>
  SCHEDULE_HEALTH_BY_DRILL[drillLevel] ?? SCHEDULE_HEALTH_BY_DRILL.region;

export const getKcphPerformanceDataByDrillLevel = (drillLevel: ChartDrillLevel): KCPHGroupedRow[] =>
  KCPH_PERFORMANCE_BY_DRILL[drillLevel] ?? KCPH_PERFORMANCE_BY_DRILL.region;

export const getToolAdoptionDataByDrillLevel = (drillLevel: ChartDrillLevel): BulletChartRow[] =>
  TOOL_ADOPTION_BY_DRILL[drillLevel] ?? TOOL_ADOPTION_BY_DRILL.region;

export const getSchedulingTrendData = (
  drillLevel: ChartDrillLevel,
  timeframe: ChartTimeframeGrain,
): SchedulingTrendData => {
  const byDrill = SCHEDULING_TREND_BY_DRILL_TIMEFRAME[drillLevel] ?? SCHEDULING_TREND_BY_DRILL_TIMEFRAME.region;
  return byDrill[timeframe] ?? byDrill.period;
};

// ========== EFFICIENCY BREAKDOWN (MOCKUP-ALIGNED) ==========

export interface EfficiencyVarianceGridRow {
  label: string;
  values: number[];
}

export interface EfficiencyVarianceGridData {
  columnLabels: string[];
  rows: EfficiencyVarianceGridRow[];
}

export interface KcphTrendSeriesData {
  labels: string[];
  estimated: number[];
  scheduled: number[];
  actual: number[];
  target: number;
}

export interface LocationLevelEfficiencyRow {
  region: string;
  market: string;
  location: string;
  year: string;
  period: string;
  week: string;
  category: string;
  zone: string;
  forecastedCases: number;
  actualCases: number;
  forecastedHours: number;
  scheduleHours: number;
  estimatedHours: number;
  actualHours: number;
  siteStandardHours: number;
}

const EFFICIENCY_VARIANCE_BY_DRILL: Record<ChartDrillLevel, EfficiencyVarianceGridData> = {
  region: {
    columnLabels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    rows: [
      { label: 'Receiving', values: [18, 22, 9, 4, 6, 12, 2] },
      { label: 'Replen', values: [8, 11, 6, -3, 2, 5, -1] },
      { label: 'Transfull', values: [-2, 3, 1, -6, -4, 2, -8] },
      { label: 'Bulk', values: [4, 6, -2, -5, 3, 9, 1] },
      { label: 'Layer', values: [-6, -4, -9, -12, -7, -3, -5] },
      { label: 'Chilled', values: [12, 9, 5, 2, 7, 15, 6] },
    ],
  },
  market: {
    columnLabels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    rows: [
      { label: 'Receiving', values: [16, 19, 8, 3, 5, 11, 1] },
      { label: 'Replen', values: [7, 10, 5, -2, 1, 4, -2] },
      { label: 'Transfull', values: [-3, 2, 0, -7, -5, 1, -9] },
      { label: 'Bulk', values: [3, 5, -3, -6, 2, 8, 0] },
      { label: 'Layer', values: [-7, -5, -10, -13, -8, -4, -6] },
      { label: 'Chilled', values: [10, 8, 4, 1, 6, 13, 5] },
    ],
  },
  location: {
    columnLabels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    rows: [
      { label: 'Receiving', values: [14, 16, 7, 2, 4, 9, 0] },
      { label: 'Replen', values: [6, 8, 4, -3, 1, 3, -3] },
      { label: 'Transfull', values: [-4, 1, -1, -8, -6, 0, -10] },
      { label: 'Bulk', values: [2, 4, -4, -7, 1, 6, -1] },
      { label: 'Layer', values: [-8, -6, -11, -14, -9, -5, -7] },
      { label: 'Chilled', values: [8, 6, 3, 1, 5, 11, 4] },
    ],
  },
};

const KCPH_TREND_BY_DRILL_TIMEFRAME: Record<ChartDrillLevel, Record<ChartTimeframeGrain, KcphTrendSeriesData>> = {
  region: {
    year: {
      labels: ['2025', '2026'],
      estimated: [96, 96],
      scheduled: [101, 99],
      actual: [89, 103],
      target: 90,
    },
    period: {
      labels: ['P1', 'P2', 'P3', 'P4', 'P5', 'P6'],
      estimated: [96, 96, 96, 96, 96, 96],
      scheduled: [101, 100, 99, 101, 103, 102],
      actual: [89, 91, 93, 95, 99, 102],
      target: 90,
    },
    week: {
      labels: ['W17', 'W18', 'W19'],
      estimated: [96, 96, 96],
      scheduled: [101, 99, 103],
      actual: [89, 93, 103],
      target: 90,
    },
    day: {
      labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      estimated: [95, 96, 95, 96, 97, 96, 95],
      scheduled: [100, 99, 100, 101, 102, 101, 100],
      actual: [90, 91, 92, 95, 98, 101, 99],
      target: 90,
    },
  },
  market: {
    year: {
      labels: ['2025', '2026'],
      estimated: [95, 96],
      scheduled: [100, 98],
      actual: [88, 101],
      target: 90,
    },
    period: {
      labels: ['P1', 'P2', 'P3', 'P4', 'P5', 'P6'],
      estimated: [95, 95, 96, 96, 96, 96],
      scheduled: [100, 99, 98, 99, 101, 100],
      actual: [88, 90, 92, 94, 97, 100],
      target: 90,
    },
    week: {
      labels: ['W17', 'W18', 'W19'],
      estimated: [95, 95, 96],
      scheduled: [100, 98, 101],
      actual: [88, 92, 100],
      target: 90,
    },
    day: {
      labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      estimated: [94, 95, 95, 96, 96, 95, 94],
      scheduled: [99, 98, 99, 100, 101, 100, 99],
      actual: [89, 90, 91, 93, 95, 98, 97],
      target: 90,
    },
  },
  location: {
    year: {
      labels: ['2025', '2026'],
      estimated: [94, 95],
      scheduled: [99, 97],
      actual: [87, 99],
      target: 90,
    },
    period: {
      labels: ['P1', 'P2', 'P3', 'P4', 'P5', 'P6'],
      estimated: [94, 94, 95, 95, 95, 95],
      scheduled: [99, 98, 97, 98, 100, 99],
      actual: [87, 89, 91, 93, 95, 98],
      target: 90,
    },
    week: {
      labels: ['W17', 'W18', 'W19'],
      estimated: [94, 94, 95],
      scheduled: [99, 97, 100],
      actual: [87, 91, 99],
      target: 90,
    },
    day: {
      labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      estimated: [93, 94, 94, 95, 95, 94, 93],
      scheduled: [98, 97, 98, 99, 100, 99, 98],
      actual: [88, 89, 90, 92, 94, 96, 95],
      target: 90,
    },
  },
};

const LOCATION_LEVEL_EFFICIENCY_BY_DRILL: Record<ChartDrillLevel, LocationLevelEfficiencyRow[]> = {
  region: [
    {
      region: 'West', market: 'S.West / Hawaii', location: 'Tucson AZ', year: '2026', period: '5', week: '20', category: 'Indirect', zone: 'Replen',
      forecastedCases: 14563, actualCases: 15210, forecastedHours: 118, scheduleHours: 123, estimatedHours: 114, actualHours: 121, siteStandardHours: 120,
    },
    {
      region: 'North', market: 'Dallas', location: 'DAL WH 01', year: '2026', period: '5', week: '20', category: 'Direct', zone: 'Receiving',
      forecastedCases: 18210, actualCases: 17642, forecastedHours: 136, scheduleHours: 141, estimatedHours: 133, actualHours: 138, siteStandardHours: 137,
    },
    {
      region: 'South', market: 'Nashville', location: 'NSH WH 11', year: '2026', period: '5', week: '20', category: 'Indirect', zone: 'Chilled',
      forecastedCases: 12640, actualCases: 12110, forecastedHours: 109, scheduleHours: 112, estimatedHours: 106, actualHours: 111, siteStandardHours: 110,
    },
  ],
  market: [
    {
      region: 'North', market: 'Dallas', location: 'DAL WH 01', year: '2026', period: '5', week: '20', category: 'Direct', zone: 'Receiving',
      forecastedCases: 18210, actualCases: 17642, forecastedHours: 136, scheduleHours: 141, estimatedHours: 133, actualHours: 138, siteStandardHours: 137,
    },
    {
      region: 'North', market: 'Chicago', location: 'CHI WH 09', year: '2026', period: '5', week: '20', category: 'Indirect', zone: 'Bulk',
      forecastedCases: 16120, actualCases: 15870, forecastedHours: 128, scheduleHours: 131, estimatedHours: 124, actualHours: 129, siteStandardHours: 127,
    },
    {
      region: 'West', market: 'Phoenix', location: 'PHX WH 04', year: '2026', period: '5', week: '20', category: 'Direct', zone: 'Layer',
      forecastedCases: 14870, actualCases: 15322, forecastedHours: 121, scheduleHours: 126, estimatedHours: 119, actualHours: 123, siteStandardHours: 122,
    },
  ],
  location: [
    {
      region: 'North', market: 'Dallas', location: 'DAL WH 01', year: '2026', period: '5', week: '20', category: 'Direct', zone: 'Receiving',
      forecastedCases: 18210, actualCases: 17642, forecastedHours: 136, scheduleHours: 141, estimatedHours: 133, actualHours: 138, siteStandardHours: 137,
    },
    {
      region: 'North', market: 'Dallas', location: 'DAL WH 02', year: '2026', period: '5', week: '20', category: 'Indirect', zone: 'Replen',
      forecastedCases: 16980, actualCases: 16420, forecastedHours: 129, scheduleHours: 132, estimatedHours: 125, actualHours: 130, siteStandardHours: 129,
    },
    {
      region: 'North', market: 'Chicago', location: 'CHI WH 09', year: '2026', period: '5', week: '20', category: 'Indirect', zone: 'Bulk',
      forecastedCases: 16120, actualCases: 15870, forecastedHours: 128, scheduleHours: 131, estimatedHours: 124, actualHours: 129, siteStandardHours: 127,
    },
  ],
};

export const getEfficiencyVarianceByDrillLevel = (drillLevel: ChartDrillLevel): EfficiencyVarianceGridData =>
  EFFICIENCY_VARIANCE_BY_DRILL[drillLevel] ?? EFFICIENCY_VARIANCE_BY_DRILL.region;

export const getEfficiencyKcphTrendData = (
  drillLevel: ChartDrillLevel,
  timeframe: ChartTimeframeGrain,
): KcphTrendSeriesData => {
  const byDrill = KCPH_TREND_BY_DRILL_TIMEFRAME[drillLevel] ?? KCPH_TREND_BY_DRILL_TIMEFRAME.region;
  return byDrill[timeframe] ?? byDrill.week;
};

export const getLocationLevelEfficiencyRows = (drillLevel: ChartDrillLevel): LocationLevelEfficiencyRow[] =>
  LOCATION_LEVEL_EFFICIENCY_BY_DRILL[drillLevel] ?? LOCATION_LEVEL_EFFICIENCY_BY_DRILL.region;

// ========== HOURS BREAKDOWN (MOCKUP-ALIGNED) ==========

export interface DirectIndirectHoursRow {
  label: string;
  plmRecommended: number;
  forecasted: number;
  scheduled: number;
  actual: number;
}

export interface OtherFixedHoursRow {
  label: string;
  forecasted: number;
  scheduled: number;
  actual: number;
}

export interface HoursByCategoryTableRow {
  region: string;
  market: string;
  location: string;
  year: string;
  period: string;
  week: string;
  category: string;
  zone: string;
  scheduleHours: number;
  actualHours: number;
  forecastedHours: number;
  estimatedHours: number;
  size: string;
  actualCases: number;
  routes: number;
  siteSqft: number;
}

const HOURS_DIRECT_INDIRECT_WEEK_BASE: DirectIndirectHoursRow[] = [
  { label: 'Direct', plmRecommended: 598, forecasted: 649, scheduled: 601, actual: 438 },
  { label: 'Indirect', plmRecommended: 205, forecasted: 210, scheduled: 206, actual: 200 },
];

const HOURS_OTHER_FIXED_WEEK_BASE: OtherFixedHoursRow[] = [
  { label: 'Other / Fixed', forecasted: 203, scheduled: 186, actual: 209 },
];

const HOURS_TIMEFRAME_FACTOR: Record<ChartTimeframeGrain, number> = {
  day: 0.22,
  week: 1,
  period: 4.3,
  year: 52,
};

const HOURS_DRILL_FACTOR: Record<ChartDrillLevel, number> = {
  region: 1,
  market: 0.94,
  location: 0.88,
};

const HOURS_CATEGORY_BY_DRILL: Record<ChartDrillLevel, HoursByCategoryTableRow[]> = {
  region: [
    {
      region: 'Northeast', market: 'DMV', location: 'Jessup MD', year: '2026', period: '5', week: '17', category: 'Indirect', zone: 'Replen',
      scheduleHours: 60, actualHours: 86, forecastedHours: 90, estimatedHours: 88, size: 'L', actualCases: 0, routes: 0, siteSqft: 95381,
    },
    {
      region: 'Northeast', market: 'DMV', location: 'Jessup MD', year: '2026', period: '5', week: '18', category: 'Indirect', zone: 'Replen',
      scheduleHours: 60, actualHours: 88, forecastedHours: 95, estimatedHours: 92, size: 'L', actualCases: 0, routes: 0, siteSqft: 95381,
    },
    {
      region: 'Texoma', market: 'OK / NTX', location: 'Oklahoma City OK', year: '2026', period: '5', week: '17', category: 'Indirect', zone: 'Receiving',
      scheduleHours: 56, actualHours: 103, forecastedHours: 71, estimatedHours: 75, size: 'S', actualCases: 20, routes: 0, siteSqft: 35760,
    },
    {
      region: 'Texoma', market: 'OK / NTX', location: 'Oklahoma City OK', year: '2026', period: '5', week: '19', category: 'Indirect', zone: 'Receiving',
      scheduleHours: 56, actualHours: 131, forecastedHours: 75, estimatedHours: 76, size: 'S', actualCases: 20, routes: 0, siteSqft: 35760,
    },
  ],
  market: [
    {
      region: 'Northeast', market: 'DMV', location: 'Jessup MD', year: '2026', period: '5', week: '17', category: 'Indirect', zone: 'Replen',
      scheduleHours: 58, actualHours: 84, forecastedHours: 88, estimatedHours: 86, size: 'L', actualCases: 0, routes: 0, siteSqft: 95381,
    },
    {
      region: 'Northeast', market: 'DMV', location: 'Jessup MD', year: '2026', period: '5', week: '18', category: 'Indirect', zone: 'Replen',
      scheduleHours: 59, actualHours: 86, forecastedHours: 92, estimatedHours: 90, size: 'L', actualCases: 0, routes: 0, siteSqft: 95381,
    },
    {
      region: 'Texoma', market: 'OK / NTX', location: 'Oklahoma City OK', year: '2026', period: '5', week: '17', category: 'Indirect', zone: 'Receiving',
      scheduleHours: 54, actualHours: 99, forecastedHours: 69, estimatedHours: 73, size: 'S', actualCases: 18, routes: 0, siteSqft: 35760,
    },
  ],
  location: [
    {
      region: 'Northeast', market: 'DMV', location: 'Jessup MD', year: '2026', period: '5', week: '17', category: 'Indirect', zone: 'Replen',
      scheduleHours: 57, actualHours: 83, forecastedHours: 87, estimatedHours: 85, size: 'L', actualCases: 0, routes: 0, siteSqft: 95381,
    },
    {
      region: 'Texoma', market: 'OK / NTX', location: 'Oklahoma City OK', year: '2026', period: '5', week: '17', category: 'Indirect', zone: 'Receiving',
      scheduleHours: 53, actualHours: 96, forecastedHours: 67, estimatedHours: 71, size: 'S', actualCases: 17, routes: 0, siteSqft: 35760,
    },
  ],
};

const scaleHoursValue = (value: number, timeframe: ChartTimeframeGrain, drillLevel: ChartDrillLevel): number => {
  const timeframeFactor = HOURS_TIMEFRAME_FACTOR[timeframe] ?? 1;
  const drillFactor = HOURS_DRILL_FACTOR[drillLevel] ?? 1;
  return Math.round(value * timeframeFactor * drillFactor);
};

export const getDirectIndirectHoursData = (
  drillLevel: ChartDrillLevel,
  timeframe: ChartTimeframeGrain,
): DirectIndirectHoursRow[] =>
  HOURS_DIRECT_INDIRECT_WEEK_BASE.map((row) => ({
    label: row.label,
    plmRecommended: scaleHoursValue(row.plmRecommended, timeframe, drillLevel),
    forecasted: scaleHoursValue(row.forecasted, timeframe, drillLevel),
    scheduled: scaleHoursValue(row.scheduled, timeframe, drillLevel),
    actual: scaleHoursValue(row.actual, timeframe, drillLevel),
  }));

export const getOtherFixedHoursData = (
  drillLevel: ChartDrillLevel,
  timeframe: ChartTimeframeGrain,
): OtherFixedHoursRow[] =>
  HOURS_OTHER_FIXED_WEEK_BASE.map((row) => ({
    label: row.label,
    forecasted: scaleHoursValue(row.forecasted, timeframe, drillLevel),
    scheduled: scaleHoursValue(row.scheduled, timeframe, drillLevel),
    actual: scaleHoursValue(row.actual, timeframe, drillLevel),
  }));

export const getHoursByCategoryRows = (drillLevel: ChartDrillLevel): HoursByCategoryTableRow[] =>
  HOURS_CATEGORY_BY_DRILL[drillLevel] ?? HOURS_CATEGORY_BY_DRILL.region;

// ========== CREWING EXECUTION (MOCKUP-ALIGNED) ==========

export interface ExecutionRecapMetric {
  label: string;
  value: string;
  subtext: string;
}

export interface ExecutionQuadrantPoint {
  label: string;
  adoptionPct: number;
  kcphIndex: number;
  withTool: boolean;
}

export interface ExecutionTrendData {
  labels: string[];
  kcphIndexPct: number[];
  adoptionPct: number[];
}

export interface ExecutionQualityMetricsRow {
  region: string;
  market: string;
  location: string;
  year: string;
  period: string;
  week: string;
  category: string;
  zone: string;
  forecastedHours: number;
  scheduledHours: number;
  estimatedHours: number;
  actualHours: number;
  crewingAccuracy: number;
  crewingEfficiency: number;
  crewingCompliance: number;
}

export interface ExecutionSubmissionDetailsRow {
  location: string;
  period: string;
  week: string;
  submissions: number;
  modificationCount: number;
  lastSubmitted: string;
  lastModified: string;
}

const EXECUTION_RECAP_BY_DRILL: Record<ChartDrillLevel, { eligible: number; active: number; submitted: number; maintained: number }> = {
  region: { eligible: 351, active: 214, submitted: 178, maintained: 121 },
  market: { eligible: 198, active: 124, submitted: 101, maintained: 72 },
  location: { eligible: 96, active: 61, submitted: 53, maintained: 38 },
};

const EXECUTION_RECAP_TIMEFRAME_FACTOR: Record<ChartTimeframeGrain, number> = {
  year: 1,
  period: 0.92,
  week: 0.88,
  day: 0.75,
};

const EXECUTION_QUADRANT_BY_DRILL: Record<ChartDrillLevel, ExecutionQuadrantPoint[]> = {
  region: [
    { label: 'Unit 1', adoptionPct: 12, kcphIndex: 114, withTool: false },
    { label: 'Canada', adoptionPct: 11, kcphIndex: 103, withTool: false },
    { label: 'Texoma', adoptionPct: 50, kcphIndex: 99, withTool: false },
    { label: 'Northeast', adoptionPct: 57, kcphIndex: 95, withTool: true },
    { label: 'West', adoptionPct: 63, kcphIndex: 95, withTool: true },
    { label: 'Unit 2', adoptionPct: 73, kcphIndex: 102, withTool: true },
    { label: 'Southeast', adoptionPct: 50, kcphIndex: 77, withTool: false },
    { label: 'Atlantic', adoptionPct: 58, kcphIndex: 75, withTool: false },
    { label: 'Northcentral', adoptionPct: 11, kcphIndex: 83, withTool: false },
  ],
  market: [
    { label: 'Dallas', adoptionPct: 66, kcphIndex: 103, withTool: true },
    { label: 'Chicago', adoptionPct: 61, kcphIndex: 97, withTool: true },
    { label: 'Phoenix', adoptionPct: 49, kcphIndex: 92, withTool: false },
    { label: 'Nashville', adoptionPct: 52, kcphIndex: 89, withTool: false },
  ],
  location: [
    { label: 'DAL WH 01', adoptionPct: 76, kcphIndex: 106, withTool: true },
    { label: 'DAL WH 02', adoptionPct: 61, kcphIndex: 98, withTool: true },
    { label: 'CHI WH 09', adoptionPct: 67, kcphIndex: 101, withTool: true },
    { label: 'PHX WH 04', adoptionPct: 44, kcphIndex: 86, withTool: false },
    { label: 'NSH WH 11', adoptionPct: 58, kcphIndex: 93, withTool: false },
  ],
};

const EXECUTION_TREND_BY_TIMEFRAME: Record<ChartTimeframeGrain, ExecutionTrendData> = {
  year: {
    labels: ['2025', '2026'],
    kcphIndexPct: [114, 116],
    adoptionPct: [69, 71],
  },
  period: {
    labels: ['P1', 'P2', 'P3', 'P4', 'P5', 'P6'],
    kcphIndexPct: [111, 115, 116, 116, 116, 116],
    adoptionPct: [76, 87, 80, 45, 62, 71],
  },
  week: {
    labels: ['W17', 'W18', 'W19', 'W20', 'W21'],
    kcphIndexPct: [111, 115, 116, 116, 116],
    adoptionPct: [76, 87, 80, 0, 71],
  },
  day: {
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    kcphIndexPct: [112, 113, 114, 114, 115, 115, 116],
    adoptionPct: [69, 73, 75, 71, 68, 66, 70],
  },
};

const EXECUTION_QUALITY_BY_DRILL: Record<ChartDrillLevel, ExecutionQualityMetricsRow[]> = {
  region: [
    {
      region: 'West', market: 'S.West / Hawaii', location: 'Tucson AZ', year: '2026', period: '5', week: '20', category: 'Indirect', zone: 'Replen',
      forecastedHours: 118, scheduledHours: 123, estimatedHours: 114, actualHours: 114, crewingAccuracy: 95.9, crewingEfficiency: 89.6, crewingCompliance: 95.9,
    },
    {
      region: 'West', market: 'S.West / Hawaii', location: 'Tucson AZ', year: '2026', period: '5', week: '19', category: 'Indirect', zone: 'Replen',
      forecastedHours: 112, scheduledHours: 116, estimatedHours: 109, actualHours: 109, crewingAccuracy: 96.6, crewingEfficiency: 90.8, crewingCompliance: 96.6,
    },
    {
      region: 'Canada', market: 'Canada', location: 'Calgary AB CS', year: '2026', period: '5', week: '17', category: 'Indirect', zone: 'Transfull',
      forecastedHours: 96, scheduledHours: 100, estimatedHours: 93, actualHours: 111, crewingAccuracy: 90.1, crewingEfficiency: 83.8, crewingCompliance: 96.0,
    },
    {
      region: 'Atlantic', market: 'Virginia', location: 'River City VA', year: '2026', period: '5', week: '17', category: 'Indirect', zone: 'Replen',
      forecastedHours: 93, scheduledHours: 97, estimatedHours: 90, actualHours: 100, crewingAccuracy: 97.0, crewingEfficiency: 90.0, crewingCompliance: 95.9,
    },
  ],
  market: [
    {
      region: 'West', market: 'S.West / Hawaii', location: 'Tucson AZ', year: '2026', period: '5', week: '20', category: 'Indirect', zone: 'Replen',
      forecastedHours: 118, scheduledHours: 123, estimatedHours: 114, actualHours: 114, crewingAccuracy: 95.9, crewingEfficiency: 89.6, crewingCompliance: 95.9,
    },
    {
      region: 'Unit 2', market: 'Florida Georgia', location: 'Jacksonville FL', year: '2026', period: '5', week: '20', category: 'Indirect', zone: 'Replen',
      forecastedHours: 101, scheduledHours: 105, estimatedHours: 98, actualHours: 109, crewingAccuracy: 96.3, crewingEfficiency: 89.9, crewingCompliance: 96.2,
    },
    {
      region: 'Atlantic', market: 'Virginia', location: 'River City VA', year: '2026', period: '5', week: '17', category: 'Indirect', zone: 'Replen',
      forecastedHours: 93, scheduledHours: 97, estimatedHours: 90, actualHours: 100, crewingAccuracy: 97.0, crewingEfficiency: 90.0, crewingCompliance: 95.9,
    },
  ],
  location: [
    {
      region: 'West', market: 'S.West / Hawaii', location: 'Tucson AZ', year: '2026', period: '5', week: '20', category: 'Indirect', zone: 'Replen',
      forecastedHours: 118, scheduledHours: 123, estimatedHours: 114, actualHours: 114, crewingAccuracy: 95.9, crewingEfficiency: 89.6, crewingCompliance: 95.9,
    },
    {
      region: 'Unit 2', market: 'Florida Georgia', location: 'Jacksonville FL', year: '2026', period: '5', week: '20', category: 'Indirect', zone: 'Replen',
      forecastedHours: 101, scheduledHours: 105, estimatedHours: 98, actualHours: 109, crewingAccuracy: 96.3, crewingEfficiency: 89.9, crewingCompliance: 96.2,
    },
  ],
};

const EXECUTION_SUBMISSION_BY_DRILL: Record<ChartDrillLevel, ExecutionSubmissionDetailsRow[]> = {
  region: [
    { location: 'Aberdeen SD', period: 'P5', week: 'W21', submissions: 1, modificationCount: 37, lastSubmitted: '05/27/26', lastModified: '--' },
    { location: 'Ada OK', period: 'P5', week: 'W21', submissions: 1, modificationCount: 41, lastSubmitted: '05/27/26', lastModified: '05/29/26' },
    { location: 'Alamosa CO', period: 'P5', week: 'W21', submissions: 1, modificationCount: 7, lastSubmitted: '05/27/26', lastModified: '--' },
    { location: 'Albany NY', period: 'P5', week: 'W21', submissions: 1, modificationCount: 8, lastSubmitted: '05/27/26', lastModified: '--' },
    { location: 'Allentown PA', period: 'P5', week: 'W21', submissions: 1, modificationCount: 12, lastSubmitted: '05/27/26', lastModified: '05/29/26' },
    { location: 'Alpena MI', period: 'P5', week: 'W21', submissions: 1, modificationCount: 14, lastSubmitted: '05/27/26', lastModified: '05/29/26' },
    { location: 'Altoona PA', period: 'P5', week: 'W21', submissions: 1, modificationCount: 17, lastSubmitted: '05/27/26', lastModified: '--' },
  ],
  market: [
    { location: 'Aberdeen SD', period: 'P5', week: 'W21', submissions: 1, modificationCount: 19, lastSubmitted: '05/27/26', lastModified: '--' },
    { location: 'Ada OK', period: 'P5', week: 'W21', submissions: 1, modificationCount: 21, lastSubmitted: '05/27/26', lastModified: '05/29/26' },
    { location: 'Alamosa CO', period: 'P5', week: 'W21', submissions: 1, modificationCount: 7, lastSubmitted: '05/27/26', lastModified: '--' },
  ],
  location: [
    { location: 'Aberdeen SD', period: 'P5', week: 'W21', submissions: 1, modificationCount: 11, lastSubmitted: '05/27/26', lastModified: '--' },
    { location: 'Ada OK', period: 'P5', week: 'W21', submissions: 1, modificationCount: 12, lastSubmitted: '05/27/26', lastModified: '05/29/26' },
  ],
};

export const getExecutionRecapMetrics = (
  drillLevel: ChartDrillLevel,
  timeframe: ChartTimeframeGrain,
): ExecutionRecapMetric[] => {
  const base = EXECUTION_RECAP_BY_DRILL[drillLevel] ?? EXECUTION_RECAP_BY_DRILL.region;
  const factor = EXECUTION_RECAP_TIMEFRAME_FACTOR[timeframe] ?? 1;
  const active = Math.round(base.active * factor);
  const submitted = Math.round(base.submitted * factor);
  const maintained = Math.round(base.maintained * factor);
  return [
    { label: 'Eligible locations', value: `${base.eligible}`, subtext: 'baseline' },
    { label: 'Active in tool', value: `${active}`, subtext: `${Math.round((active / Math.max(1, base.eligible)) * 100)}% of prior` },
    { label: 'Submitted this period', value: `${submitted}`, subtext: `${Math.round((submitted / Math.max(1, active)) * 100)}% of prior` },
    { label: 'Maintained after submit', value: `${maintained}`, subtext: `${Math.round((maintained / Math.max(1, submitted)) * 100)}% of prior` },
  ];
};

export const getExecutionQuadrantPoints = (drillLevel: ChartDrillLevel): ExecutionQuadrantPoint[] =>
  EXECUTION_QUADRANT_BY_DRILL[drillLevel] ?? EXECUTION_QUADRANT_BY_DRILL.region;

export const getExecutionTrendData = (
  _drillLevel: ChartDrillLevel,
  timeframe: ChartTimeframeGrain,
): ExecutionTrendData => EXECUTION_TREND_BY_TIMEFRAME[timeframe] ?? EXECUTION_TREND_BY_TIMEFRAME.week;

export const getExecutionQualityMetricsRows = (drillLevel: ChartDrillLevel): ExecutionQualityMetricsRow[] =>
  EXECUTION_QUALITY_BY_DRILL[drillLevel] ?? EXECUTION_QUALITY_BY_DRILL.region;

export const getExecutionSubmissionRows = (drillLevel: ChartDrillLevel): ExecutionSubmissionDetailsRow[] =>
  EXECUTION_SUBMISSION_BY_DRILL[drillLevel] ?? EXECUTION_SUBMISSION_BY_DRILL.region;
